import { useMemo } from "react";
import intl from "react-intl-universal";
import {
  TbActivity,
  TbAlertTriangle,
  TbCircleCheck,
  TbClock,
  TbCoin,
  TbNetwork,
  TbPercentage,
  TbWallet,
} from "react-icons/tb";
import { SummaryCard } from "../../../../../Components/Atoms/SummaryCard";
import {
  buildBaasStableCdnIconUrl,
  resolveBaasStableIconUrl,
} from "../../../../../Services/baasStablesWalletApi";
import { formatStable } from "../../../Stables/constants";
import { BaasHomeProcessingSparkline } from "../../Processing/components/BaasHomeProcessingSparkline";
import {
  buildBaasStablesActivitySnapshot,
  buildBaasStablesWalletHealth,
  getBaasStablesRailMetrics,
} from "../baasStablesMetrics";
import { BaasStableOperationBadge, BaasStableStatusBadge } from "./BaasStableStatusBadge";
import { BaasStablesNetworkBadge } from "./BaasStablesNetworkBadge";

/** Wallet / rail StableBalance amounts — never formatMonetaryValue / R$ (BRL1 ≠ fiat). */
const formatWalletStableAmount = (value, asset) =>
  `${formatStable(value ?? 0)}${asset ? ` ${asset}` : ""}`.trim();

const formatPercent = (value, fractionDigits = 1) =>
  `${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)}%`;

const formatCount = (value) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value ?? 0);

/** Brand coin art at CDN colors (no gold filter — it collapses USDC into a solid circle). */
const AssetIcon = ({ asset, iconUrl, size = 20 }) => {
  const src =
    resolveBaasStableIconUrl(iconUrl, asset) || buildBaasStableCdnIconUrl(asset);
  if (!src) {
    return (
      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#e7a73f]/15 text-[10px] font-bold text-[#e7a73f]">
        {String(asset || "?").slice(0, 1)}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className="h-5 w-5 shrink-0 rounded-full object-cover"
      loading="lazy"
    />
  );
};

/**
 * Full Stables monitoring surface.
 * Wallet health, activity, and rail volumes use live wallets/transactions.
 */
export const BaasStablesMetricsPanel = ({
  wallets = [],
  transactions = [],
  compact = false,
}) => {
  const rail = useMemo(() => getBaasStablesRailMetrics(transactions), [transactions]);
  const health = useMemo(() => buildBaasStablesWalletHealth(wallets), [wallets]);
  const activity = useMemo(
    () => buildBaasStablesActivitySnapshot(transactions),
    [transactions],
  );

  const volumeCards = useMemo(
    () => [
      {
        key: "volumeUsdc",
        icon: TbCoin,
        title: intl.get("baas.stablesWallet.metrics.volumeUsdc"),
        value: formatWalletStableAmount(rail.volumeUsdc, "USDC"),
      },
      {
        key: "volumeUsdt",
        icon: TbCoin,
        title: intl.get("baas.stablesWallet.metrics.volumeUsdt"),
        value: formatWalletStableAmount(rail.volumeUsdt, "USDT"),
      },
      {
        key: "txCount",
        icon: TbActivity,
        title: intl.get("baas.stablesWallet.metrics.txCount"),
        value: formatCount(rail.txCount),
        footerLabel: intl.get("baas.stablesWallet.metrics.periodHint"),
      },
      {
        key: "successRate",
        icon: TbPercentage,
        title: intl.get("baas.stablesWallet.metrics.successRate"),
        value: formatPercent(rail.successRate),
        footerLabel: intl.get("baas.stablesWallet.metrics.periodHint"),
      },
    ],
    [rail],
  );

  const healthCards = useMemo(
    () => [
      {
        key: "totalWallets",
        icon: TbWallet,
        title: intl.get("baas.stablesWallet.metrics.walletsTotal"),
        value: formatCount(health.total),
        footerLabel: intl.get("baas.stablesWallet.metrics.walletsLive"),
      },
      {
        key: "active",
        icon: TbCircleCheck,
        title: intl.get("baas.stablesWallet.metrics.walletsActive"),
        value: formatCount(health.byStatus.active),
      },
      {
        key: "pending",
        icon: TbClock,
        title: intl.get("baas.stablesWallet.metrics.walletsPending"),
        value: formatCount(health.byStatus.pending),
      },
      {
        key: "blocked",
        icon: TbAlertTriangle,
        title: intl.get("baas.stablesWallet.metrics.walletsBlocked"),
        value: formatCount(health.byStatus.blocked + health.byStatus.inactive),
      },
    ],
    [health],
  );

  if (compact) {
    return (
      <section className="min-w-0 w-full space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="dashboard-section-label mb-1">
              {intl.get("baas.stablesWallet.metrics.section")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.stablesWallet.metrics.compactSubtitle")}
            </p>
          </div>
        </div>
        <div className="dashboard-card-grid">
          {volumeCards.slice(0, 4).map((card) => (
            <SummaryCard
              key={card.key}
              icon={card.icon}
              title={card.title}
              value={card.value}
              footerLabel={card.footerLabel}
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="min-w-0 space-y-6">
      <section className="min-w-0 w-full space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="dashboard-section-label mb-1">
              {intl.get("baas.stablesWallet.metrics.railSection")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.stablesWallet.metrics.subtitle")}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
            <TbActivity className="h-3.5 w-3.5" aria-hidden />
            {intl.get("baas.stablesWallet.metrics.activityLive")}
          </span>
        </div>

        <div className="dashboard-card-grid">
          {volumeCards.map((card) => (
            <SummaryCard
              key={card.key}
              icon={card.icon}
              title={card.title}
              value={card.value}
              footerLabel={card.footerLabel}
            />
          ))}
        </div>

        {rail.trendVolume?.length ? (
          <div className="app-card p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {intl.get("baas.stablesWallet.metrics.trendTitle")}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.stablesWallet.metrics.periodHint")}
              </p>
            </div>
            <div className="h-16">
              <BaasHomeProcessingSparkline values={rail.trendVolume} currency="BRL" />
            </div>
          </div>
        ) : null}
      </section>

      <section className="min-w-0 w-full space-y-3">
        <div>
          <p className="dashboard-section-label mb-1">
            {intl.get("baas.stablesWallet.metrics.healthSection")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.stablesWallet.metrics.healthSubtitle")}
          </p>
        </div>

        <div className="dashboard-card-grid">
          {healthCards.map((card) => (
            <SummaryCard
              key={card.key}
              icon={card.icon}
              title={card.title}
              value={card.value}
              footerLabel={card.footerLabel}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="app-card p-4">
            <p className="mb-3 text-sm font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.stablesWallet.metrics.byAsset")}
            </p>
            {health.byAsset.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.stablesWallet.metrics.noWallets")}
              </p>
            ) : (
              <ul className="space-y-3">
                {health.byAsset.map((item) => (
                  <li
                    key={item.asset}
                    className="flex min-w-0 items-center justify-between gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0 dark:border-brand-dark-border"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <AssetIcon asset={item.asset} iconUrl={item.iconUrl} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                          {item.asset}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {intl.get("baas.stablesWallet.metrics.walletCount", {
                            count: formatCount(item.count),
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-bold text-brand-dark dark:text-gray-100">
                        {formatWalletStableAmount(item.available, item.asset)}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {intl.get("baas.stablesWallet.metrics.blockedShort", {
                          amount: formatWalletStableAmount(item.blocked, item.asset),
                        })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="app-card p-4">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-brand-dark dark:text-gray-100">
              <TbNetwork className="h-4 w-4 text-[#e7a73f]" aria-hidden />
              {intl.get("baas.stablesWallet.metrics.byNetwork")}
            </p>
            {health.byNetwork.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.stablesWallet.metrics.noWallets")}
              </p>
            ) : (
              <ul className="space-y-3">
                {health.byNetwork.map((item) => (
                  <li
                    key={item.network}
                    className="flex items-center justify-between gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0 dark:border-brand-dark-border"
                  >
                    <BaasStablesNetworkBadge network={item.network} />
                    <span className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                      {formatCount(item.count)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="min-w-0 w-full space-y-3">
        <div>
          <p className="dashboard-section-label mb-1">
            {intl.get("baas.stablesWallet.metrics.activitySection")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.stablesWallet.metrics.activitySubtitle")}
          </p>
        </div>

        <div className="dashboard-card-grid">
          <SummaryCard
            icon={TbActivity}
            title={intl.get("baas.stablesWallet.metrics.activityTotal")}
            value={formatCount(activity.total)}
            footerLabel={intl.get("baas.stablesWallet.metrics.activityLive")}
          />
          <SummaryCard
            icon={TbCircleCheck}
            title={intl.get("baas.stablesWallet.metrics.activityProcessed")}
            value={formatCount(activity.processed)}
            footerLabel={formatPercent(activity.successRate)}
          />
          <SummaryCard
            icon={TbClock}
            title={intl.get("baas.stablesWallet.metrics.activityPending")}
            value={formatCount(activity.pending)}
          />
          <SummaryCard
            icon={TbAlertTriangle}
            title={intl.get("baas.stablesWallet.metrics.activityFailed")}
            value={formatCount(activity.failed)}
          />
        </div>

        {activity.recent.length ? (
          <div className="app-card overflow-hidden">
            <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {intl.get("baas.stablesWallet.metrics.recentOps")}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-brand-dark-elevated/50 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">
                      {intl.get("baas.stablesWallet.recentActivity.columns.type")}
                    </th>
                    <th className="px-4 py-2.5 font-medium">
                      {intl.get("baas.stablesWallet.recentActivity.columns.asset")}
                    </th>
                    <th className="px-4 py-2.5 font-medium">
                      {intl.get("baas.stablesWallet.recentActivity.columns.amount")}
                    </th>
                    <th className="px-4 py-2.5 font-medium">
                      {intl.get("baas.stablesWallet.recentActivity.columns.status")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
                  {activity.recent.map((tx) => (
                    <tr key={tx.id}>
                      <td className="px-4 py-2.5">
                        <BaasStableOperationBadge operation={tx.operation} />
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <AssetIcon asset={tx.asset} />
                          {tx.asset}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-medium text-brand-dark dark:text-gray-100">
                        {formatWalletStableAmount(tx.amount, tx.asset)}
                      </td>
                      <td className="px-4 py-2.5">
                        <BaasStableStatusBadge status={tx.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.stablesWallet.metrics.noActivity")}
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default BaasStablesMetricsPanel;
